/**
 * AWS CodeCommit integration types (organization-level connection).
 *
 * One connection = one AWS account in one region. An organization may hold several
 * connections. Repository `full_name` is `{account_id}/{region}/{repo_name}`, so the
 * backend can resolve the connection for a repository without any extra field.
 */

export type CodeCommitAuthMode = 'access_keys' | 'iam_role';

export interface CodeCommitConnection {
  id: string;
  aws_account_id: string;
  aws_region: string;
  auth_mode: CodeCommitAuthMode;
  label?: string | null;
  caller_arn?: string | null;
  credentials_invalid?: boolean;
  connected_at?: string;
  connected_by?: string;
}

export interface OrgCodeCommitStatus {
  connected: boolean;
  connections: CodeCommitConnection[];
}

export interface CodeCommitRoleSetup {
  /** The Kolega principal the customer's role must trust. */
  kolega_principal_arn: string;
  /** Per-organization external ID the trust policy must require. */
  external_id: string;
}

export interface CodeCommitConnectPayload {
  aws_region: string;
  auth_mode: CodeCommitAuthMode;
  label?: string;
  access_key_id?: string;
  secret_access_key?: string;
  role_arn?: string;
}

export interface CodeCommitConnectResponse {
  connected: boolean;
  connection: CodeCommitConnection;
}

/** Commercial regions where CodeCommit is offered. Default selection is the first entry. */
export const CODECOMMIT_REGIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'us-east-1', label: 'US East (N. Virginia) — us-east-1' },
  { id: 'us-east-2', label: 'US East (Ohio) — us-east-2' },
  { id: 'us-west-1', label: 'US West (N. California) — us-west-1' },
  { id: 'us-west-2', label: 'US West (Oregon) — us-west-2' },
  { id: 'ca-central-1', label: 'Canada (Central) — ca-central-1' },
  { id: 'sa-east-1', label: 'South America (São Paulo) — sa-east-1' },
  { id: 'eu-west-1', label: 'Europe (Ireland) — eu-west-1' },
  { id: 'eu-west-2', label: 'Europe (London) — eu-west-2' },
  { id: 'eu-west-3', label: 'Europe (Paris) — eu-west-3' },
  { id: 'eu-central-1', label: 'Europe (Frankfurt) — eu-central-1' },
  { id: 'eu-north-1', label: 'Europe (Stockholm) — eu-north-1' },
  { id: 'eu-south-1', label: 'Europe (Milan) — eu-south-1' },
  { id: 'ap-east-1', label: 'Asia Pacific (Hong Kong) — ap-east-1' },
  { id: 'ap-south-1', label: 'Asia Pacific (Mumbai) — ap-south-1' },
  { id: 'ap-northeast-1', label: 'Asia Pacific (Tokyo) — ap-northeast-1' },
  { id: 'ap-northeast-2', label: 'Asia Pacific (Seoul) — ap-northeast-2' },
  { id: 'ap-northeast-3', label: 'Asia Pacific (Osaka) — ap-northeast-3' },
  { id: 'ap-southeast-1', label: 'Asia Pacific (Singapore) — ap-southeast-1' },
  { id: 'ap-southeast-2', label: 'Asia Pacific (Sydney) — ap-southeast-2' },
  { id: 'af-south-1', label: 'Africa (Cape Town) — af-south-1' },
  { id: 'me-south-1', label: 'Middle East (Bahrain) — me-south-1' },
  { id: 'il-central-1', label: 'Israel (Tel Aviv) — il-central-1' },
];

export const DEFAULT_CODECOMMIT_REGION = CODECOMMIT_REGIONS[0].id;

/** IAM role ARN in the commercial partition, e.g. arn:aws:iam::123456789012:role/KolegaCodeCommit */
export const ROLE_ARN_PATTERN = /^arn:aws:iam::\d{12}:role\/[\w+=,.@/-]+$/;

/** Soft check for access key IDs (long-term AKIA…, temporary ASIA…). Used for hints only. */
export const ACCESS_KEY_ID_PATTERN = /^(AKIA|ASIA)[A-Z0-9]{16}$/;

export const codecommitAuthModeLabel = (mode: CodeCommitAuthMode): string =>
  mode === 'iam_role' ? 'IAM role' : 'Access keys';

/** "{label} ({account} · {region})" or "{account} · {region}". */
export const codecommitConnectionDisplayName = (conn: CodeCommitConnection): string => {
  const base = `${conn.aws_account_id} · ${conn.aws_region}`;
  return conn.label ? `${conn.label} (${base})` : base;
};

export const codecommitConsoleUrl = (region: string): string =>
  `https://${region}.console.aws.amazon.com/codesuite/codecommit/repositories?region=${region}`;

/** Trust policy JSON the customer pastes into their IAM role for the assume-role mode. */
export const codecommitTrustPolicy = (setup: CodeCommitRoleSetup): string =>
  JSON.stringify(
    {
      Version: '2012-10-17',
      Statement: [
        {
          Effect: 'Allow',
          Principal: { AWS: setup.kolega_principal_arn },
          Action: 'sts:AssumeRole',
          Condition: { StringEquals: { 'sts:ExternalId': setup.external_id } },
        },
      ],
    },
    null,
    2
  );
